import React, { useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  formatDetectionTime,
  inspectionInfoStorage,
} from "../../services/ocr/InspectionInfoStorageModel";
import { Alert, Box, Button, Card, CardMedia, Chip, Divider, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import { Add, Delete, Edit, PhotoCamera } from "@mui/icons-material";
import { translationService } from "../../services/translation/TranslationService";
import { snackbarNotificationsService } from "../../services/notifications/SnackbarNotificationsService";
import { OcrService } from "../../services/ocr/OcrService";
import { videoPlayerService } from "../../services/video-player/VideoPlayerService";
import { ocrTsToSeconds, secondsToOcrTimestamp } from "./playbackSync";
import DetectionEditDialog, { EditableDetection } from "./DetectionEditDialog";
import { SegmentationService } from "../../services/segmentation/SegmentationService";
import { Segmentation } from "../../api-models/response/VideoAnalysisDataResponse";
import ImageSegmentationOverlay from "./ImageSegmentationOverlay";
import {
  DEFAULT_DETECTION_STATUS,
  conditionColor,
  riskColor,
  statusColor,
} from "./detectionAssessment";
import {
  OcrCodedField,
  changedCodedFields,
  codedByKey,
  codedFromDisplayRow,
  machineCounterpart,
} from "./detectionProvenance";
import WarningBeforeAction from "../data-components/WarningBeforeAction";
import { ApprovedList } from "./useApprovedList";
import SaveListButton from "./SaveListButton";
import { isDataUrl } from "./detectionFrames";
import { identityOf } from "./ocrRowIdentity";
import { ReviewedDetectionDto } from "../../services/ocr/InspectionReviewService";

interface IDetectionListProps {
  inspectionInsightStorage: typeof inspectionInfoStorage;
  // When provided, per-card Edit/Delete and an Add (grab frame) action appear;
  // edits flow into ocrService.detections (and the Comparison tab's baseline).
  ocrService?: OcrService;
  // When the user enables "mark damage", the segmentation service supplying the
  // masks plus the confidence-filtered list to draw. Each card's frame then gets
  // the damage polygons for that detection's timestamp drawn on top.
  segmentationEnabled?: boolean;
  segmentation?: SegmentationService;
  filteredSegmentations?: Segmentation[];
  /**
   * The approved list for the OCR side, and the only way to change anything here.
   *
   * Every edit — including one made while the raw OCR output is on screen — is written to the
   * approved list. ocr.json keeps the machine's original output. (The ocrService mutators this
   * used to call never persisted anyway: they changed MST state only, so those edits were
   * silently lost on reload.)
   */
  approved?: ApprovedList;
  /** True while the rows shown are the raw ocr.json output rather than the approved list. */
  showingMachineOutput?: boolean;
  /** The station at a video position, from the analysis run's distance track (if any). */
  distanceForTime?: (seconds: number) => number | undefined;
}

const t = (key: string) => translationService.translate(key);

const statusLabel = (value?: string) =>
  t(`comparison.review.status.${(value ?? DEFAULT_DETECTION_STATUS) === "InReview"
    ? "in-review"
    : (value ?? DEFAULT_DETECTION_STATUS).toLowerCase()}`);

const renderField = (label: string, value?: string | number, edited?: boolean) => {
  if (value === undefined || value === "") return <></>;
  return (
    <Grid container spacing = {1} alignItems= "flex-start">
      <Grid item>
        <Typography variant="body2" fontWeight="bold">
          {label}:
        </Typography>
      </Grid>
      <Grid item>
        <Typography variant="body2" >
          {value}
        </Typography>
      </Grid>
      {/* A marked field is one a human set. It is a label rather than a colour on the value,
          because the PDF export is a greyscale-safe capture of this DOM and a colour alone would
          not survive it — nor serve a colour-blind reader. */}
      {edited && (
        <Grid item>
          <Chip
            size="small"
            variant="outlined"
            color="primary"
            label={translationService.translate("comparison.detection.field-edited")}
          />
        </Grid>
      )}
    </Grid>
  );
};

// Rows now carry the reviewer's verdict directly (same as the analysis side), so there is no
// separate assessment map to consult.
const toEditable = (d: any): Partial<EditableDetection> => ({
  main_Code: d.main_Code ?? "",
  distance: Number(d.distance ?? 0),
  timestamp: d.timestamp ?? "",
  text: d.text ?? "",
  c1: d.c1 ?? "",
  c2: d.c2 ?? "",
  q1: d.q1 ?? "",
  q2: d.q2 ?? "",
  sa: d.sa ?? "",
  postionFrom: d.postionFrom ?? "",
  positionTo: d.positionTo ?? "",
  frameUrl: d.frameUrl ?? "",
  detectionStatus: d.detectionStatus ?? DEFAULT_DETECTION_STATUS,
  conditionRating: d.conditionRating ?? "",
  riskRating: d.riskRating ?? "",
});

/**
 * An edited OCR-shaped row, as the review stores it.
 *
 * `previous` carries the row's ORIGINAL station and main code so the approved list can match it
 * even when the edit changed one of those — they are what forms its identity.
 */
const toReviewedDetection = (
  e: EditableDetection,
  previous?: any
): ReviewedDetectionDto => ({
  source: previous?.source || "OCR",
  // An untouched OCR reading becomes Edited once corrected; a row the reviewer created is Added.
  decision: previous ? (previous.decision === "Added" ? "Added" : "Edited") : "Added",
  mainCode: e.main_Code,
  distance: Number(e.distance) || 0,
  // Unconditional, the way the seed rows are built. Guarded, a row whose timestamp is blank
  // keyed as `2.43|BAB` once edited while its seed keyed as `2.43|BAB|0` -- the same mismatch
  // identityOf had, one edit later.
  timeSeconds: ocrTsToSeconds(e.timestamp),
  note: e.text || undefined,
  c1: e.c1,
  c2: e.c2,
  q1: e.q1,
  q2: e.q2,
  sa: e.sa,
  positionFrom: e.postionFrom,
  positionTo: e.positionTo,
  // Frame handling, split by what the value actually IS rather than where it came from.
  //
  // Only inline bytes belong in capturedFrame — storing an expiring link there is what dropped
  // the image from the approved package. Anything else is a reference and goes to machineFrameUrl.
  // `previous` is a DISPLAY row here (activeDetections), which spells the frame `frameUrl` and
  // carries neither capturedFrame nor machineFrameUrl, so reading those off it silently lost the
  // picture on every edit — hence keepFrame, which reads whichever spelling is present.
  ...keepFrame(e.frameUrl, previous),
  detectionStatus: e.detectionStatus,
  // "" means "not assessed"; send undefined so the field is absent rather than unparseable.
  conditionRating: e.conditionRating || undefined,
  riskRating: e.riskRating || undefined,
});

/**
 * The frame fields for a saved row, from the dialog's value and whatever the row already had.
 *
 * Accepts the previous row in either shape — the display row spells it `frameUrl`, the review DTO
 * splits it into `capturedFrame` / `machineFrameUrl` — so an edit never loses the image just
 * because the two shapes disagree on a name.
 */
const keepFrame = (
  edited: string | undefined,
  previous: any
): Pick<ReviewedDetectionDto, "capturedFrame" | "machineFrameUrl" | "frameIndex"> => {
  const previousInline = isDataUrl(previous?.capturedFrame)
    ? previous.capturedFrame
    : isDataUrl(previous?.frameUrl)
      ? previous.frameUrl
      : undefined;
  const previousLink = previous?.machineFrameUrl
    ?? (previous?.frameUrl && !isDataUrl(previous.frameUrl) ? previous.frameUrl : undefined);

  return {
    capturedFrame: isDataUrl(edited) ? edited : previousInline,
    // A non-data value from the dialog is a link to an existing frame, not new bytes.
    machineFrameUrl: !isDataUrl(edited) && edited ? edited : previousLink,
    frameIndex: previous?.frameIndex,
  };
};


export const DetectionList = observer(({
  inspectionInsightStorage,
  ocrService,
  segmentationEnabled,
  segmentation,
  filteredSegmentations,
  approved,
  showingMachineOutput,
  distanceForTime,
}: IDetectionListProps) => {
  // Editable on the APPROVED view only.
  //
  // The machine output is the source of truth and is shown to be read: offering Edit / Delete /
  // Add on it invited the reading that ocr.json was being changed, which it never is. Worse now
  // that saving also signs the review off — an edit made while looking at the machine output
  // would be approved in the same gesture, from a screen that looked like the original.
  const editable = !!approved && !showingMachineOutput;
  const busy = !!approved?.saving;
  const showSegmentation =
    !!segmentationEnabled && !!segmentation?.segmentationSuccessful;
  // Whether a card shows the status / condition / risk chips.
  //
  // Never on the machine output. A verdict is something a person reaches; ocr.json carries none,
  // so every one of its rows fell back to "Status: Draft / Condition: — / Risk: —" — three chips
  // per card saying nothing, on the one view that exists to show what the machine read. (The
  // `hasApprovedList` clause below was defeating the very thing its comment claimed: as soon as
  // the file had an approved list, it painted the raw rows too.)
  //
  // On the approved list, every card carries all three once the file has one, so an unset field
  // reads "—" rather than vanishing — which is indistinguishable from "assessed as fine".
  const showAssessment = (d: any) =>
    !showingMachineOutput &&
    (inspectionInsightStorage.hasApprovedList ||
      !!(d.detectionStatus || d.conditionRating || d.riskRating));
  // Which coded fields on each row a human changed, measured against the machine output. Built
  // once per render rather than per row, and only on the approved view: on the machine output
  // there is nothing to compare against, and every field would come back unchanged anyway.
  //
  // Keyed by station + main code, which is a row's identity — so a row the reviewer added has no
  // counterpart here, and neither does one whose station or main code was itself the correction.
  // The row carries its ORIGINAL key for exactly that reason (see stableKey), so an edit to those
  // fields still finds the machine row it came from.
  //
  // Built from the SNAPSHOT, never from the MST array itself: setDetections fills that array in
  // place, so the node keeps ONE identity for the life of the store and a memo keyed on it
  // computes exactly once -- on the first render, while the store is still empty. That left an
  // empty map here, no approved row found its machine counterpart, and "no counterpart" used to
  // mean "mark every field" -- which is why every field on every card read as edited.
  const machineByKey = useMemo(
    () => codedByKey(inspectionInsightStorage.detectionRows, codedFromDisplayRow),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inspectionInsightStorage.detectionRows]
  );
  const changedFieldsFor = (row: any): Set<OcrCodedField> => {
    if (showingMachineOutput) return new Set();
    const coded = codedFromDisplayRow(row);
    const machine = machineCounterpart(coded, row?.stableKey, machineByKey);

    // No counterpart: the reviewer created this row, and the card already says so once with its
    // own badge. Marking all nine fields as well states nothing per field and drowns out the
    // marks on the rows where a mark does mean something.
    //
    // It is also the honest answer when the row is an EDIT whose counterpart cannot be found --
    // claiming every field changed is a guess, and the row's badge still reads Edited.
    if (!machine) return new Set();

    return changedCodedFields(coded, machine);
  };

  const [showResetToOcr, setShowResetToOcr] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addInitial, setAddInitial] = useState<Partial<EditableDetection> | null>(null);
  const [capturing, setCapturing] = useState(false);

  // Grab the current video frame, then open the Add dialog pre-seeded with it.
  const openAdd = async () => {
    const vid = ocrService?.fileId;
    const seconds = videoPlayerService.videoProgress;
    let frameUrl = "";
    if (vid != null) {
      setCapturing(true);
      try {
        frameUrl = (await (videoPlayerService as any).captureScreenshotAt(vid, seconds)) || "";
      } catch {
        /* handled below, same as an empty result */
      } finally {
        setCapturing(false);
      }
    }

    // Said out loud rather than swallowed. A silent failure produced a frameless row with no
    // explanation, and nothing to distinguish a capture that failed from a damage that simply has
    // no picture. The row is still worth adding — its camera button captures the frame afterwards.
    if (!frameUrl) {
      snackbarNotificationsService.showError("comparison.detection.capture-failed");
    }
    // A row the reviewer adds by hand belongs to the approved list, so it starts Approved —
    // left at the "Draft" default it would be filed away and never appear.
    setAddInitial({
      frameUrl,
      timestamp: secondsToOcrTimestamp(seconds),
      // Left empty so the reviewer states the station. Seeding 0 handed every added damage the
      // same valid-looking station, which is also half of the row's identity.
      distance: "",
      detectionStatus: "Approved",
    });
    setAddOpen(true);
  };

  return (
    <Grid container spacing={2} sx={{ padding: 2 }} id = "pdf-content-list">
       <Grid item xs={12} id="pdf-content-list-header" sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Typography variant="h6" sx={{ textAlign: "left", display: "block" }}>
            {translationService.translate("ocrService.inspectionInfo.detectionList")}
        </Typography>
        {/* Rows the reviewer moved off Approved. They are still stored (and Approve all brings
            them back) — they are simply not part of the inspection any more. */}
        {!!approved?.notApprovedCount && (
          <Typography variant="caption" color="text.secondary" data-html2canvas-ignore>
            {t("comparison.approved-list.not-approved")}: {approved.notApprovedCount}
          </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {editable && (
          <>
            {approved!.dirty && (
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label={t("comparison.approved-list.unsaved")}
                data-html2canvas-ignore
              />
            )}
            <Button
              startIcon={capturing ? <PhotoCamera /> : <Add />}
              variant="outlined"
              size="small"
              disabled={capturing || busy}
              onClick={openAdd}
              data-html2canvas-ignore
            >
              {t("ocrService.detection.add")}
            </Button>
            {/* Nothing here is written until this is pressed — see useApprovedList. */}
            <SaveListButton approved={approved!} />
            <Button
              variant="text"
              size="small"
              disabled={!approved!.dirty || busy}
              onClick={approved!.discard}
              data-html2canvas-ignore
            >
              {t("comparison.approved-list.discard")}
            </Button>
            {/* The way back to the machine output. Approve all cannot do this: it unions, so a
                bad edit survives it. Confirmed first, because it drops every correction and every
                row the reviewer added. */}
            <Button
              variant="text"
              size="small"
              color="warning"
              disabled={busy}
              onClick={() => setShowResetToOcr(true)}
              data-html2canvas-ignore
            >
              {t("comparison.approved-list.reset-to-ocr")}
            </Button>
          </>
        )}
      </Grid>
      {/* One notice, saying whichever thing is true of the view you are on: that this is the
          untouchable original and where to go to change something, or that your edits are local
          until you save them. */}
      {!!approved && (
        <Grid item xs={12} data-html2canvas-ignore>
          <Alert severity="info">
            {t(
              showingMachineOutput
                ? "comparison.detection-source.read-only"
                : "comparison.detection-source.edits-go-to-approved"
            )}
          </Alert>
        </Grid>
      )}
      {/* The approved list is empty: say why and how to fill it, rather than leaving a blank
          page under a finished OCR run. */}
      {!showingMachineOutput && inspectionInsightStorage.activeDetections.length === 0 && (
        <Grid item xs={12}>
          <Alert severity="info">{t("comparison.approved-list.empty")}</Alert>
        </Grid>
      )}
        {inspectionInsightStorage.activeDetections.map((detection: any, index: number) => {
          const changed = changedFieldsFor(detection);
          return (
          <Grid item xs={12} key={index}>
              <Card sx={{ display: "flex", mb: 2, flexDirection: "row", borderRadius: 2, border: "1px solid black", boxShadow: 8, overflow: "hidden" }} id="pdf-content-card">
                <Grid container>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 2 }}>
                      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1 }}>
                        {detection.addedByUser && (
                          <Chip
                            size="small"
                            color="primary"
                            variant="outlined"
                            label={t("comparison.detection.added-by-reviewer")}
                          />
                        )}
                        {/* The reviewer's verdict, read straight off the row — the same way the
                            analysis side does it. These used to be columns on the Inspection
                            Graph; they belong here, next to the frame the verdict was formed
                            from. All three are labelled and always present once the file has
                            an approved list, so an unset field reads "—" instead of vanishing
                            (which is indistinguishable from "assessed as fine"). */}
                        {showAssessment(detection) && (
                          <>
                            <Chip
                              size="small"
                              variant="outlined"
                              color={statusColor(detection.detectionStatus ?? DEFAULT_DETECTION_STATUS)}
                              label={`${t("detection.assessment.status")}: ${statusLabel(
                                detection.detectionStatus
                              )}`}
                            />
                            <Chip
                              size="small"
                              variant="outlined"
                              color={conditionColor(detection.conditionRating)}
                              label={`${t("detection.assessment.condition")}: ${
                                detection.conditionRating
                                  ? t(`detection.condition.${detection.conditionRating.toLowerCase()}`)
                                  : "—"
                              }`}
                            />
                            <Chip
                              size="small"
                              variant="outlined"
                              color={riskColor(detection.riskRating)}
                              label={`${t("detection.assessment.risk")}: ${
                                detection.riskRating
                                  ? t(`detection.risk.${detection.riskRating.toLowerCase()}`)
                                  : "—"
                              }`}
                            />
                          </>
                        )}
                      </Box>
                      {editable && (
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }} data-html2canvas-ignore>
                          <Tooltip title={t("general.edit")}>
                            <IconButton
                              size="small"
                              aria-label="edit"
                              disabled={busy}
                              onClick={() => setEditIndex(index)}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("general.delete")}>
                            <IconButton
                              size="small"
                              aria-label="delete"
                              disabled={busy}
                              onClick={() => approved?.remove(identityOf(detection))}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                      {renderField(
                        translationService.translate("ocrService.inspectionInfo.time"),
                        formatDetectionTime(detection.timestamp)
                      )}
                      {renderField(translationService.translate("ocrService.inspectionInfo.distance"), detection.distance, changed.has("distance"))}
                      {renderField("Main Code", detection.main_Code, changed.has("mainCode"))}

                      <Divider sx={{ my: 1, borderBottom: "1px solid black", borderStyle: "dashed" }} />

                      {renderField("C1", detection.c1, changed.has("c1"))}
                      {renderField("C2", detection.c2, changed.has("c2"))}
                      {renderField("SA", detection.sa, changed.has("sa"))}
                      {renderField("Q1", detection.q1, changed.has("q1"))}
                      {renderField("Q2", detection.q2, changed.has("q2"))}
                      {renderField(translationService.translate("ocrService.inspectionInfo.positionFrom"), detection.postionFrom, changed.has("positionFrom"))}
                      {renderField(translationService.translate("ocrService.inspectionInfo.positionTo"), detection.positionTo, changed.has("positionTo"))}

                      <Divider sx={{ my: 1, borderBottom: "1px solid black", borderStyle: "dashed" }} />

                      {renderField(translationService.translate("ocrService.inspectionInfo.text"), detection.text)}
                   </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} sx={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 2 }}>
                    {showSegmentation && detection.frameUrl ? (
                      <ImageSegmentationOverlay
                        imageSrc={detection.frameUrl}
                        timestampSeconds={ocrTsToSeconds(detection.timestamp)}
                        videoSegmentation={segmentation!}
                        filteredSegmentations={filteredSegmentations ?? []}
                      />
                    ) : detection.frameUrl ? (
                      <CardMedia
                        component="img"
                        image={detection.frameUrl}
                        alt="frame"
                        sx={{ width: "100%", height: "auto", objectFit: "cover" }}
                      />
                    ) : (
                      // No frame: say so. Rendering the img with an empty src produced a broken
                      // image icon, which reads as "the picture failed to load" rather than
                      // "this observation has no picture".
                      <Typography variant="caption" color="text.secondary">
                        {t("comparison.detection.no-frame")}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Card>
          </Grid>
          );
        })}

      {editable && (
        <>
          <WarningBeforeAction
            titleKey="comparison.approved-list.reset-to-ocr"
            isVisible={showResetToOcr}
            toggleModal={() => setShowResetToOcr(false)}
            buttonOk={{
              textKey: "comparison.approved-list.reset-to-ocr",
              action: () => {
                setShowResetToOcr(false);
                approved!.resetToMachine();
              },
            }}
          >
            <div>{t("comparison.approved-list.reset-to-ocr-confirm")}</div>
          </WarningBeforeAction>
          <DetectionEditDialog
            distanceForTime={distanceForTime}
            open={editIndex !== null}
            title={t("general.edit")}
            initial={
              editIndex !== null
                ? toEditable(inspectionInsightStorage.activeDetections[editIndex])
                : null
            }
            codeOptions={[]}
            onSave={(d) => {
              if (editIndex !== null) {
                const previous = inspectionInsightStorage.activeDetections[editIndex];
                approved?.upsert(toReviewedDetection(d, previous), identityOf(previous));
              }
              setEditIndex(null);
            }}
            onCancel={() => setEditIndex(null)}
          />
          <DetectionEditDialog
            distanceForTime={distanceForTime}
            open={addOpen}
            title={t("ocrService.detection.add")}
            initial={addInitial}
            codeOptions={[]}
            onSave={(d) => {
              approved?.upsert(toReviewedDetection(d));
              setAddOpen(false);
            }}
            onCancel={() => setAddOpen(false)}
          />
        </>
      )}
    </Grid>
  );
});
